import React, { useState, useRef } from "react";

import { Formik, Form, Field } from "formik";
import { toast } from "../../services/toast";

import { makeStyles } from 'tss-react/mui';
import { green } from "@mui/material/colors";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Rating from '@mui/material/Rating';
import Chip from "@mui/material/Chip";

import PersonIcon from "@mui/icons-material/Person";
import AndroidIcon from "@mui/icons-material/Android";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAltOutlined";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import BlockIcon from "@mui/icons-material/Block";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import LoopIcon from "@mui/icons-material/Loop";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";

import PropTypes from "prop-types";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  auditTypeContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  auditTypeBtn: {
    flex: 1,
    padding: theme.spacing(1.2),
    borderRadius: 8,
    border: "2px solid",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    fontWeight: 600,
    fontSize: "0.9rem",
    transition: "all 0.2s",
    background: "transparent",
  },
  auditTypeBtnUser: {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    "&.selected": {
      background: theme.palette.primary.main,
      color: "#fff",
    },
  },
  auditTypeBtnIa: {
    borderColor: "#7b1fa2",
    color: "#7b1fa2",
    "&.selected": {
      background: "#7b1fa2",
      color: "#fff",
    },
  },
  sectionLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  classificationContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  classificationChip: {
    fontWeight: 600,
    fontSize: "0.82rem",
    "&:focus": { outline: "none" },
  },
  orDivider: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    margin: theme.spacing(1.5, 0),
    color: theme.palette.text.disabled,
    fontSize: "0.75rem",
    fontWeight: 600,
    "&::before, &::after": {
      content: '""',
      flex: 1,
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  ratingDisabled: {
    opacity: 0.35,
    pointerEvents: "none",
  },
}));

const customIcons = {
  1: { icon: <SentimentVeryDissatisfiedIcon fontSize="large" />, label: "Muy insatisfecho" },
  2: { icon: <SentimentDissatisfiedIcon fontSize="large" />, label: "Insatisfecho" },
  3: { icon: <SentimentSatisfiedIcon fontSize="large" />, label: "Neutral" },
  4: { icon: <SentimentSatisfiedAltIcon fontSize="large" />, label: "Satisfecho" },
  5: { icon: <SentimentVerySatisfiedIcon fontSize="large" />, label: "Muy satisfecho" },
};

function IconContainer(props) {
  const { value, ...other } = props;
  return <span {...other}>{customIcons[value].icon}</span>;
}
IconContainer.propTypes = { value: PropTypes.number.isRequired };

const CLASSIFICATIONS = [
  { rate: -1, label: "Spam",           icon: <BlockIcon fontSize="small" />,             color: "#e53935" },
  { rate: -2, label: "Sin respuesta",  icon: <PhoneDisabledIcon fontSize="small" />,     color: "#f57c00" },
  { rate: -4, label: "Escalado",       icon: <ErrorOutlineIcon fontSize="small" />,      color: "#1976d2" },
  { rate: -5, label: "Reincidente",    icon: <LoopIcon fontSize="small" />,              color: "#7b1fa2" },
  { rate: -6, label: "Sin clasificar", icon: <HelpOutlineIcon fontSize="small" />,       color: "#757575" },
];

const StarModal = ({ open, onClose, ticketId, companyId, idUser }) => {
  const { classes } = useStyles();

  const [auditType, setAuditType] = useState("user");
  const greetingRef = useRef();

  const initialState = {
    rated: 0,
    observation: "",
    ticketId,
    companyId,
    AuditorUserId: idUser,
  };

  const handleClose = () => {
    onClose();
    setAuditType("user");
  };

  const handleSaveStar = async (values) => {
    try {
      await api.post("/start", { ...values, auditType });
      toast.success("Calificación asignada correctamente");
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        maxWidth="sm"
        fullWidth
        open={open}
        onClose={handleClose}
        scroll="paper"
      >
        <DialogTitle>Panel de Auditoría</DialogTitle>

        <Paper>
          <Formik
            initialValues={initialState}
            enableReinitialize
            onSubmit={async (values, actions) => {
              await handleSaveStar(values);
              actions.setSubmitting(false);
            }}
          >
            {({ isSubmitting, setFieldValue, values }) => (
              <Form>
                <DialogContent dividers>

                  {}
                  <Typography className={classes.sectionLabel}>
                    Tipo de auditoría
                  </Typography>
                  <Box className={classes.auditTypeContainer} sx={{
                    mb: 2
                  }}>
                    <button
                      type="button"
                      className={`${classes.auditTypeBtn} ${classes.auditTypeBtnUser}${auditType === "user" ? " selected" : ""}`}
                      onClick={() => setAuditType("user")}
                    >
                      <PersonIcon fontSize="small" />
                      Usuario
                    </button>
                    <button
                      type="button"
                      className={`${classes.auditTypeBtn} ${classes.auditTypeBtnIa}${auditType === "ia" ? " selected" : ""}`}
                      onClick={() => setAuditType("ia")}
                    >
                      <AndroidIcon fontSize="small" />
                      IA
                    </button>
                  </Box>

                  {}
                  <Typography className={classes.sectionLabel}>
                    Calificación
                  </Typography>
                  <Box className={values.rated < 0 ? classes.ratingDisabled : ""} sx={{
                    mb: 0
                  }}>
                    <Rating
                      name="rated"
                      value={values.rated > 0 ? values.rated : 0}
                      onChange={(event, value) => setFieldValue("rated", value ?? 0)}
                      style={{ color: "red" }}
                      slotProps={{
                        icon: {
                          component: IconContainer
                        }
                      }}
                    />
                  </Box>

                  {}
                  <div className={classes.orDivider}>
                    Sin datos suficientes para calificar
                  </div>

                  {}
                  <Box className={classes.classificationContainer} sx={{
                    mb: 1
                  }}>
                    {CLASSIFICATIONS.map((item) => {
                      const selected = values.rated === item.rate;
                      return (
                        <Chip
                          key={item.rate}
                          icon={React.cloneElement(item.icon, {
                            style: { color: selected ? "#fff" : item.color },
                          })}
                          label={item.label}
                          clickable
                          onClick={() => setFieldValue("rated", selected ? 0 : item.rate)}
                          className={classes.classificationChip}
                          style={{
                            borderColor: item.color,
                            background: selected ? item.color : "transparent",
                            color: selected ? "#fff" : item.color,
                            border: `2px solid ${item.color}`,
                          }}
                          variant={selected ? "default" : "outlined"}
                        />
                      );
                    })}
                  </Box>

                  {}
                  <Box sx={{
                    mt: 1
                  }}>
                    <Field
                      as={TextField}
                      label="Observación"
                      multiline
                      inputRef={greetingRef}
                      rows={4}
                      fullWidth
                      name="observation"
                      variant="outlined"
                      margin="dense"
                    />
                  </Box>

                </DialogContent>

                <DialogActions>
                  <Button
                    onClick={handleClose}
                    color="secondary"
                    disabled={isSubmitting}
                    variant="outlined"
                  >
                    {i18n.t("queueModal.buttons.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.btnWrapper}
                  >
                    Calificar
                    {isSubmitting && (
                      <CircularProgress size={24} className={classes.buttonProgress} />
                    )}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </Paper>
      </Dialog>
    </div>
  );
};

export default StarModal;
